import { dateKeySaoPaulo, orderPaymentMatchKey } from './settlementImport';

/**
 * Build reconciliation rows by matching settlement entries to orders/transactions
 * in the file period.
 *
 * Match: same payment type (credit/debit/pix) + exact charged amount (cents).
 * Without authorization key, duplicate type/amount (common at fairs) are assigned
 * 1:1 arbitrarily — same calendar day first, then leftovers any day.
 */

function orderLineCandidates(order) {
    const methods = Array.isArray(order.payment_methods) ? order.payment_methods : [];
    const lines = [];
    methods.forEach((method) => {
        const matchKey = orderPaymentMatchKey(method?.name);
        if (!matchKey) {
            return;
        }
        const lineAmount = Number(method.amount) || 0;
        lines.push({
            matchKey,
            amountCents: lineAmount > 0 ? lineAmount : Number(order.amount) || 0,
        });
    });
    if (lines.length === 0) {
        const matchKey = orderPaymentMatchKey(methods[0]?.name);
        if (matchKey) {
            lines.push({ matchKey, amountCents: Number(order.amount) || 0 });
        }
    }
    return lines;
}

function transactionIsBankReconciled(tx) {
    return Boolean(tx?.bank_reconciled || (tx?.authorization_id && String(tx.authorization_id).trim()));
}

function orderIsBankReconciled(order) {
    return Boolean(order?.bank_reconciled);
}

function orderMatchesEntry(order, entry) {
    const lines = orderLineCandidates(order);
    if (lines.some((line) => line.matchKey === entry.matchKey && line.amountCents === entry.chargedCents)) {
        return true;
    }
    if (
        (Number(order.amount) || 0) === entry.chargedCents
        && lines.some((line) => line.matchKey === entry.matchKey)
    ) {
        return true;
    }
    return false;
}

function entryDateKey(entry) {
    return entry.saleDateKey || dateKeySaoPaulo(entry.transactionDate) || '';
}

/** Fisher–Yates; rng injectable for tests. */
export function shuffleArray(items, rng = Math.random) {
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    }
    return arr;
}

/**
 * Pre-assign orders to entry indexes.
 * Pass 1: same day + type + amount (random among ties).
 * Pass 2: remaining entries get remaining matching orders any day (random).
 * Returns Map<entryIndex, order>.
 */
export function assignOrdersToEntries(entrySlots, orders, rng = Math.random) {
    const assignment = new Map(); // entryIndex -> order
    const usedOrderIds = new Set();

    const takeRandom = (candidates) => {
        if (!candidates.length) {
            return null;
        }
        const picked = shuffleArray(candidates, rng)[0];
        usedOrderIds.add(String(picked.id));
        return picked;
    };

    const unusedMatching = (entry, sameDayOnly) => {
        const dateKey = entryDateKey(entry);
        return orders.filter((order) => {
            if (usedOrderIds.has(String(order.id)) || !orderMatchesEntry(order, entry)) {
                return false;
            }
            if (sameDayOnly) {
                return dateKeySaoPaulo(order.created_at) === dateKey;
            }
            return true;
        });
    };

    // Pass 1 — prioriza o mesmo dia (várias de R$20 no mesmo dia: 1:1 aleatório).
    entrySlots.forEach(({ entry, index }) => {
        const order = takeRandom(unusedMatching(entry, true));
        if (order) {
            assignment.set(index, order);
        }
    });

    // Pass 2 — sobras: outros dias, mesmo tipo/valor, 1:1 aleatório.
    entrySlots.forEach(({ entry, index }) => {
        if (assignment.has(index)) {
            return;
        }
        const order = takeRandom(unusedMatching(entry, false));
        if (order) {
            assignment.set(index, order);
        }
    });

    return assignment;
}

function describeNoOrderMatch(entry, orders, assignedOrderIds) {
    const sameTypeAmount = (orders || []).filter((order) => orderMatchesEntry(order, entry));
    if (sameTypeAmount.length === 0) {
        return 'Sem pedido — criar só a transação do extrato';
    }

    const alreadyReconciled = sameTypeAmount.filter((order) => orderIsBankReconciled(order));
    const usedByOtherLines = sameTypeAmount.filter((order) => (
        !orderIsBankReconciled(order) && assignedOrderIds.has(String(order.id))
    ));
    const stillFree = sameTypeAmount.filter((order) => (
        !orderIsBankReconciled(order) && !assignedOrderIds.has(String(order.id))
    ));

    // Ainda há venda livre: não deveria acontecer após o assign; mensagem defensiva.
    if (stillFree.length > 0) {
        return `Há ${stillFree.length} venda(s) livre(s) de mesmo tipo/valor — revisar match`;
    }

    const parts = [];
    if (usedByOtherLines.length > 0) {
        parts.push(`${usedByOtherLines.length} já usada(s) por outras linhas deste extrato`);
    }
    if (alreadyReconciled.length > 0) {
        parts.push(`${alreadyReconciled.length} já conciliada(s)`);
    }
    const detail = parts.length ? parts.join('; ') : 'nenhuma livre';
    return `Recebimento sem venda livre neste tipo/valor (${sameTypeAmount.length} no período: ${detail}) — criar só a transação`;
}

function findMatchingTransaction(entry, matchedOrder, availableTx, usedTxIds, entryDateKeyValue) {
    if (matchedOrder) {
        const linked = availableTx.find((tx) => (
            !usedTxIds.has(tx.id)
            && String(tx.order_id || '') === String(matchedOrder.id)
            && tx.type === entry.direction
        ));
        if (linked) {
            return linked;
        }
    }

    const amountOk = (tx) => {
        const txAmount = Number(tx.amount) || 0;
        const txNet = Number(tx.net_amount) || 0;
        return (
            txAmount === entry.chargedCents
            || txAmount === entry.releasedCents
            || txNet === entry.chargedCents
            || txNet === entry.releasedCents
        );
    };

    const baseFilter = (tx) => (
        !usedTxIds.has(tx.id)
        && tx.type === entry.direction
        && tx.payment_method === entry.apiPaymentMethod
        && amountOk(tx)
    );

    const sameDay = availableTx.filter((tx) => (
        baseFilter(tx)
        && entryDateKeyValue
        && dateKeySaoPaulo(tx.created_at) === entryDateKeyValue
    ));
    if (sameDay.length) {
        return shuffleArray(sameDay)[0];
    }

    const any = availableTx.filter(baseFilter);
    return any.length ? shuffleArray(any)[0] : null;
}

/**
 * @returns {Array} reconcile rows with status:
 *  skipped | already_reconciled | matched_order | no_order | orphan
 */
export function buildReconciliationRows(entries, orders, transactions, rng = Math.random) {
    const usedTxIds = new Set();

    const txByAuth = new Map();
    (transactions || []).forEach((tx) => {
        const auth = String(tx.authorization_id || '').trim();
        if (auth) {
            txByAuth.set(auth, tx);
        }
    });

    const availableOrders = (orders || []).filter((o) => !orderIsBankReconciled(o));
    const availableTx = (transactions || []).filter((tx) => !transactionIsBankReconciled(tx));

    const list = entries || [];
    const rowByIndex = new Array(list.length);

    const slotsNeedingOrder = [];

    list.forEach((entry, index) => {
        const rowKey = `${entry.id || 'row'}-${index}`;
        const dateKey = entryDateKey(entry);

        if (!entry.importable) {
            rowByIndex[index] = {
                rowKey,
                entry,
                status: 'skipped',
                statusLabel: 'Sem tipo de pagamento — não importar',
                order: null,
                transaction: null,
                canImportOrphan: false,
                importOrphan: false,
                selected: false,
            };
            return;
        }

        const existingByAuth = txByAuth.get(String(entry.id || '').trim());
        if (existingByAuth) {
            usedTxIds.add(existingByAuth.id);
            rowByIndex[index] = {
                rowKey,
                entry,
                status: 'already_reconciled',
                statusLabel: 'Já conciliado (authorization_id)',
                order: null,
                transaction: existingByAuth,
                canImportOrphan: false,
                importOrphan: false,
                selected: false,
            };
            return;
        }

        slotsNeedingOrder.push({ entry, index, dateKey });
    });

    const orderByEntryIndex = assignOrdersToEntries(slotsNeedingOrder, availableOrders, rng);
    const assignedOrderIds = new Set(
        [...orderByEntryIndex.values()].map((order) => String(order.id))
    );

    slotsNeedingOrder.forEach(({ entry, index, dateKey }) => {
        const rowKey = `${entry.id || 'row'}-${index}`;
        const matchedOrder = orderByEntryIndex.get(index) || null;
        let matchedTx = findMatchingTransaction(entry, matchedOrder, availableTx, usedTxIds, dateKey);

        if (matchedOrder) {
            if (matchedTx) {
                usedTxIds.add(matchedTx.id);
            }
            rowByIndex[index] = {
                rowKey,
                entry,
                status: 'matched_order',
                statusLabel: matchedTx
                    ? 'Pedido + transação — vincular autorização e corrigir liberado/desconto'
                    : 'Pedido encontrado — criar transação com liberado do extrato',
                order: matchedOrder,
                transaction: matchedTx,
                canImportOrphan: false,
                importOrphan: false,
                selected: true,
            };
            return;
        }

        if (matchedTx) {
            usedTxIds.add(matchedTx.id);
            rowByIndex[index] = {
                rowKey,
                entry,
                status: 'matched_transaction',
                statusLabel: entry.direction === 'income'
                    ? 'Sem pedido — vincular autorização e corrigir liberado/desconto'
                    : 'Transação — vincular autorização e corrigir liberado/desconto',
                order: null,
                transaction: matchedTx,
                canImportOrphan: false,
                importOrphan: false,
                selected: true,
            };
            return;
        }

        if (entry.direction === 'income') {
            rowByIndex[index] = {
                rowKey,
                entry,
                status: 'no_order',
                statusLabel: describeNoOrderMatch(entry, orders, assignedOrderIds),
                order: null,
                transaction: null,
                canImportOrphan: true,
                importOrphan: true,
                selected: false,
            };
            return;
        }

        rowByIndex[index] = {
            rowKey,
            entry,
            status: 'orphan',
            statusLabel: 'Sem pedido/transação — criar só a transação do extrato',
            order: null,
            transaction: null,
            canImportOrphan: true,
            importOrphan: true,
            selected: false,
        };
    });

    return rowByIndex.map((row) => {
        if (!row?.entry?.importable || row.entry.direction !== 'outcome') {
            return row;
        }
        return {
            ...row,
            outcomeCategory: row.outcomeCategory === 'withdrawal' ? 'withdrawal' : 'expense',
        };
    });
}

export function buildTransactionPayloadFromEntry(entry, orderId, outcomeCategory) {
    const amount = entry.chargedCents;
    const net = entry.releasedCents;
    // Sempre usa o extrato: bruto cobrado, líquido liberado e desconto = diferença.
    const deduction = Math.max(0, amount - net);
    const payload = {
        type: entry.direction,
        amount,
        net_amount: net,
        deduction,
        description: entry.description || entry.source || '',
        payment_method: entry.apiPaymentMethod,
        order_id: orderId || '',
        authorization_id: entry.id || '',
        source: entry.source || '',
    };
    if (entry.direction === 'outcome') {
        payload.outcome_category = outcomeCategory === 'withdrawal' ? 'withdrawal' : 'expense';
    }
    return payload;
}

export async function fetchAllPages(api, path, params = {}) {
    const limit = 100;
    let page = 1;
    let totalPages = 1;
    const items = [];

    while (page <= totalPages) {
        const query = new URLSearchParams({
            page: String(page),
            limit: String(limit),
            ...Object.fromEntries(
                Object.entries(params).filter(([, v]) => v != null && v !== '')
            ),
        }).toString();
        const response = await api.get(`${path}?${query}`);
        const data = response.data?.data || response.data || {};
        const list = data.orders || data.transactions || [];
        items.push(...list);
        totalPages = Number(data.total_pages) || 1;
        page += 1;
        if (page > 50) {
            break;
        }
    }
    return items;
}
